import React, { useState, useEffect, useRef, useMemo } from 'react'
import moment from 'moment'
import { logger } from '../../actions'
import { isEmpty } from 'lodash'
import {
  getTimeDifference,
  getFlightDuration,
  getCurrentDuration,
  getProgressTime,
  getFormattedTime,
  occurrenceDate,
  getFutureDate,
  addressMatchesFlightAirportInfo,
  isCompleteFlightNumber,
  canonicalFlightKey,
  parseFlightNumber,
  localWallClockFromUtc,
  legLocalDay,
} from './helpers'
import { CANCELLED_STATUSES } from './flightNerveConstants'
import { isFlightVerifiedForBooking } from './flightNervePure'
import { resolveFlightNerveInfo, extractIataCode } from './flightNerveResolver'
import { hadRecentTransientFlightNerveFailure } from './flightNerveClient'
import { getAirlineNameCached, getAirlineTimeZoneCached } from './apiCache'
import FlightTrackingCard from './FlightTrackingCard'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// Debounce before a typed flight number fires the (paid) external lookup
// chain. The original used 2000ms; 500ms still absorbs keystrokes while
// cutting a flat 1.5s off every search. Stale responses are handled by the
// request-id/abort guard below, not by the debounce width.
const FETCH_DEBOUNCE_MS = 500

// ── Failed-lookup latch ─────────────────────────────────────────────────────────
// MODULE-level (not a ref: remounts via key++ get fresh refs, which is exactly how
// re-search loops survive per-instance guards). If the identical lookup (flight+
// date+leg+AIRPORTS) failed within the window, settle to no-data IMMEDIATELY with zero
// network — whatever re-triggered the search, the loop starves. Success clears
// the latch so a genuine retry after the window behaves normally.
const FAILED_LOOKUP_TTL_MS = 30 * 1000
// A lookup that died on a supplier blip (5xx/timeout/quota) is not a fact about
// the flight — latch it just long enough to starve a re-trigger loop (which
// cycles in milliseconds), short enough that a human retry after the blip works.
const TRANSIENT_FAILED_LOOKUP_TTL_MS = 5 * 1000
const recentFailedLookups = new Map()

// The latch is keyed on what the user MEANT, not on what they typed. Callers pass the
// full `query|date|leg|airports` dedupe key; only the query half is canonicalised here, so change
// detection (oldQuery / lastFetchedFlightRef) keeps its exact raw-string semantics and a
// respelling still re-renders — it just stops re-running the paid lookup chain.
//
// Without this, every respelling of a flight that just failed bought the whole chain
// again: SK835 -> SK0835 -> SK835, G3 1837 -> G31837 -> g31837, AA 4588 -> AA4588 ->
// aa 4588, EK229 -> EK219 -> EK209 -> EK229. All within a minute or two, all against a
// number FlightNerve had already said no to.
const latchKey = key => {
  const parts = String(key || '').split('|')
  if (!parts.length) return key
  parts[0] = canonicalFlightKey(parts[0])
  return parts.join('|')
}

const lookupRecentlyFailed = rawKey => {
  const key = latchKey(rawKey)
  const entry = recentFailedLookups.get(key)
  if (!entry) return false
  if (Date.now() - entry.at > entry.ttl) {
    recentFailedLookups.delete(key)
    return false
  }
  return true
}
const markLookupFailed = (rawKey, { transient = false } = {}) => {
  if (!rawKey) return
  const key = latchKey(rawKey)
  const ttl = transient ? TRANSIENT_FAILED_LOOKUP_TTL_MS : FAILED_LOOKUP_TTL_MS
  recentFailedLookups.set(key, { at: Date.now(), ttl })
  if (recentFailedLookups.size > 50) {
    const oldest = recentFailedLookups.keys().next().value
    recentFailedLookups.delete(oldest)
  }
  // Deliberate breadcrumb: if this fires more than once for one user action,
  // something upstream is re-triggering searches — that is the bug to hunt.
  console.warn(`[FlightTracking] lookup failed, latched ${ttl / 1000}s:`, key)
}
const markLookupSucceeded = rawKey =>
  recentFailedLookups.delete(latchKey(rawKey))

function normFlightSearchKey(v) {
  return String(v ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
}

// ── Date reconciliation for airports FlightNerve mis-stamps ─────────────────────
// FlightNerve keys /airline by the departure date IT believes the flight has. For an
// airport it has no timezone for it stamps +00:00, so it derives that date from the UTC
// instant and every instance comes back a day out. Proven on QR1002 DOH->DXB:
//   date=20260811 -> true Doha local Wed 12 Aug 01:05   <- the flight the agent wants
//   date=20260812 -> true Doha local Thu 13 Aug 01:05
//   date=20260813 -> true Doha local Fri 14 Aug 01:05
// So a booking for the 12th matches NOTHING the agent can type, and the correct
// wall-clock (see localWallClockFromUtc) turns that into "Flight date does not match".
//
// ONE extra resolve, and never recursive. Fetch, check the BOOKED side's true local date,
// and only if it is off by a day or two fetch that adjacent day once and keep whichever
// actually matches. Deliberately not a retry loop: this file's failed-lookup latch exists
// because a re-entrant search loop has burned this component before (LA2218).
//
// Self-disabling three times over: shift 0 means no second call at all, so a correctly
// stamped airport never pays for this; it stops firing entirely once FlightNerve populates
// the timezone; and if the adjacent day does not match either, the original result stands.
const bookedLegOf = (legs, side) => {
  if (!Array.isArray(legs)) return null
  const entry = legs.find(leg => leg && leg[side])
  return entry ? entry[side] : null
}

// The calendar day this leg happens on, at its own airport.
//
// Was: take the UTC field in preference and convert it into the airport's timezone. That is
// wrong for the primary provider and was silently producing off-by-one days.
// flightapi.io sends no UTC field at all and labels the LOCAL wall clock with a zero
// offset — EK206 arrives Dubai at "23:10, Aug 26" as 2026-08-26T23:10:00-00:00. Parsing
// that as an instant and converting to Asia/Dubai gives 03:10 on the 27th, so this returned
// the 27th, reconcileBookedDate saw a one-day shift that did not exist, and re-queried or
// discarded a correct result — breaking exactly the overnight arrivals it exists to fix.
//
// Now reads the offset as the provider gave it, via localCalendarDate — the same helper the
// transfer surfaces and the resolver already use, whose own comment names this bug. Correct
// for BOTH providers: flightapi.io's zero-offset local clock reads as the 26th, and
// FlightNerve's real offsets (…T03:25:00+04:00) read as the 25th. Verified on paired calls
// for QR1002, EK206 and SQ317 including a 31 Dec -> 1 Jan rollover.
//
// No longer async and no longer needs a timezone: one fewer airport lookup per
// reconciliation, and no dependency on a zone we may not have.
const trueLocalDayOf = (legs, side, referenceDay) => {
  const leg = bookedLegOf(legs, side)
  if (!leg) return null
  const localIso =
    side === 'arrival' ? leg.arrivalDateTime : leg.departureDateTime
  // scheduledTime is the fallback for a leg with no ISO at all — flightapi.io returned
  // QR1002's arrival that way.
  return legLocalDay(localIso, leg.scheduledTime, referenceDay)
}

const reconcileBookedDate = async ({
  legs,
  apiDate,
  bookedDay,
  side,
  resolve,
}) => {
  try {
    if (!legs || legs.message || !bookedDay) return legs
    const resolvedDay = trueLocalDayOf(legs, side, bookedDay)
    if (!resolvedDay) return legs
    const shift = moment(resolvedDay, 'YYYY-MM-DD').diff(
      moment(bookedDay, 'YYYY-MM-DD'),
      'days'
    )
    // 0 = already right, the overwhelmingly common case. Beyond two days apart this is a
    // different flight, not an off-by-one, and walking further would invent a match.
    if (shift === 0 || Math.abs(shift) > 2) return legs
    const shiftedApiDate = moment(apiDate, 'YYYYMMDD')
      .subtract(shift, 'days')
      .format('YYYYMMDD')
    const retried = await resolve(shiftedApiDate)
    if (!retried || retried.message) return legs
    const retriedDay = trueLocalDayOf(retried, side, bookedDay)
    // Only accept a retry that actually lands on the booked day. Anything else and the
    // first answer stands, so this can never make a lookup worse.
    return retriedDay === bookedDay ? retried : legs
  } catch (reconcileError) {
    // Best-effort correction. A failure here must never lose a result we already have.
    console.log('reconcileBookedDate error', reconcileError)
    return legs
  }
}

// ── INVARIANT: one brain for flight identity ────────────────────────────────────
// Every flight key in this file — whether derived from the RAW typed query or from
// a PROCESSED payload (airline.iata + number) — must come out of parseFlightNumber,
// which now lives in ./flightNervePure so the Slack de-duper and the failed-lookup
// latch share this exact implementation rather than growing their own. The two sides of the stale-payload guards are compared with ===, so two
// normalization implementations WILL eventually disagree on some input and silently
// drop a valid payload (zero-padded "KL0792" hung the search exactly this way: the
// parse stripped the padding, a hand-rolled twin regex here did not). Delegating
// means any future parsing change — new code formats, suffix letters, whatever —
// applies to both sides at once, by construction. Do NOT inline a second
// normalization chain here, ever.
function normFlightQueryKey(v) {
  const { fname, fnumber } = parseFlightNumber(
    typeof v === 'string' ? v : String(v ?? '')
  )
  return normFlightSearchKey(`${fname}${fnumber}`)
}

function flightKeyFromProcessedFlightItem(item) {
  const airline = item?.flightInfo?.airline
  if (!airline) return ''
  return normFlightSearchKey(`${airline.iata ?? ''}${airline.number ?? ''}`)
}

function flightKeyFromFlightInfoState(fi) {
  const airline = fi?.airline
  if (!airline) return ''
  return normFlightSearchKey(`${airline.iata ?? ''}${airline.number ?? ''}`)
}

// scheduledTime arrives as "HH:MM, Mon DD"; splitting on the comma and appending the
// year gives "Mon DD YYYY HH:MM".
const SCHEDULED_TIME_FORMAT = 'MMM D YYYY HH:mm'

/**
 * Resolve the correct year for scheduledTime strings returned by flightapi.io.
 *
 * The API omits the year from scheduledTime ("4:05 AM, Jan 1"), so we derive it
 * from the YYYYMMDD date we queried with (apiDateStr).
 *
 * Edge cases handled:
 *  1. Future-year flights (e.g. booking for 2027 while current year is 2026).
 *  2. Cross-year arrivals (dep Dec 31 → arr Jan 1 means arrival is next year).
 *  3. Stale apiDate when a prev-day re-fetch crossed the year boundary
 *     (queried Jan 1 but fell back to Dec 31 → apiDate still says January).
 *
 * Returns null for departure/arrival when the corresponding scheduledTime is
 * empty or malformed (no comma separator).
 */
function resolveFlightDateTimes(depScheduled, arrScheduled, apiDateStr) {
  const baseYear = parseInt(apiDateStr.substring(0, 4), 10)
  const apiMonth = parseInt(apiDateStr.substring(4, 6), 10) // 1-12

  const [depTime, depDatePart = ''] = (depScheduled || '').split(',')
  const [arrTime, arrDatePart = ''] = (arrScheduled || '').split(',')

  // Parse against an explicit format. Without one, "Aug 14 2026 10:05" is neither
  // RFC2822 nor ISO, so moment hands it to `new Date()` — whose behaviour on this shape
  // is not specified and differs by browser. These are the times we render on the card,
  // so they cannot be left to the engine. Verified identical to the old loose parse on
  // every shape the API produces (padded and unpadded day and hour, year rollover), and
  // malformed input still comes back invalid for the guard below.
  const depMoment = depDatePart.trim()
    ? moment.utc(
        `${depDatePart.trim()} ${baseYear} ${depTime}`,
        SCHEDULED_TIME_FORMAT
      )
    : null
  const arrMoment = arrDatePart.trim()
    ? moment.utc(
        `${arrDatePart.trim()} ${baseYear} ${arrTime}`,
        SCHEDULED_TIME_FORMAT
      )
    : null

  if (depMoment) {
    // If apiDate is January but the response is December (prev-day re-fetch
    // crossed the year boundary backwards), adjust the departure year back.
    if (apiMonth === 1 && depMoment.month() === 11) {
      depMoment.subtract(1, 'year')
    }
  }

  if (arrMoment) {
    if (depMoment) {
      // Cross-year arrival: departure in December, arrival in January means
      // the arrival is in the next calendar year relative to the departure.
      if (depMoment.month() === 11 && arrMoment.month() === 0) {
        arrMoment.year(depMoment.year() + 1)
      } else {
        arrMoment.year(depMoment.year())
      }
    } else {
      // No departure to compare against — keep baseYear.
    }
  }

  return {
    departureTimeDate: depMoment ? depMoment.format() : null,
    arrivalTimeDate: arrMoment ? arrMoment.format() : null,
  }
}

// RD-4144: minutes the provider's estimate is off the schedule, per leg (negative = early).
// Both strings share one shape and one clock, so the difference holds even for the airports
// whose local string is really UTC (the DOH case below) — which is why the estimate is carried
// as an offset and applied to the corrected scheduled time, never parsed on its own.
function estimateOffsets(departInfo, arrivInfo, apiDate) {
  const scheduled = resolveFlightDateTimes(
    departInfo.scheduledTime,
    arrivInfo.scheduledTime,
    apiDate
  )
  const estimated = resolveFlightDateTimes(
    departInfo.estimatedTime || departInfo.scheduledTime,
    arrivInfo.estimatedTime || arrivInfo.scheduledTime,
    apiDate
  )
  const offset = (from, to) => {
    if (!from || !to) return 0
    const minutes = moment.utc(to).diff(moment.utc(from), 'minutes')
    // A day or more apart is a different flight instance or a bad parse, not a delay.
    return Number.isFinite(minutes) && Math.abs(minutes) < 24 * 60 ? minutes : 0
  }
  return {
    departure: offset(scheduled.departureTimeDate, estimated.departureTimeDate),
    arrival: offset(scheduled.arrivalTimeDate, estimated.arrivalTimeDate),
  }
}

const withOffset = (time, minutes) =>
  time && minutes
    ? moment
        .utc(time)
        .add(minutes, 'minutes')
        .format()
    : time

// The estimated landing the chauffeur buffer is applied to — only when it stays on the
// scheduled landing day. The booking forms write the pick-up date back and look the flight up
// again from it, so an estimate past midnight would make them search the next day's flight.
const sameDayEstimate = (scheduled, minutes) => {
  const estimate = withOffset(scheduled, minutes)
  return estimate &&
    scheduled &&
    moment.utc(estimate).format('YYYY-MM-DD') ===
      moment.utc(scheduled).format('YYYY-MM-DD')
    ? estimate
    : scheduled
}

// Does the caller describe a DEPARTURE lookup — the flight takes off from the booked
// airport — using the only unambiguous evidence available: it named a drop-off airport
// and no pick-up one.
//
// isPickUpContext used to be inferred as `type === 'pickUp' || airportPickUpInfo != null`,
// and every standalone-VIP DEPARTURE surface passes airportPickUpInfo (it carries the
// flight number / terminal for the CARD, nothing to do with direction). So a departure
// was resolved as an arrival, with two consequences: the schedule-first plan read the
// arrival hint those callers never set and was skipped, and — the real damage — a failed
// lookup RETRIED THE PREVIOUS DAY (resolver: `isPickUpContext && noData`). A departure
// never left the day before, so that retry either wastes a credit or, when the number
// happens to fly daily, SUCCEEDS and shows the customer yesterday's instance.
//
// Module scope on purpose: the legacy eslint-plugin-react-hooks in this repo mis-parses
// conditionals in the component body.
const isDepartureOnlyLookup = ({
  pickUpAddress,
  pickUpAirportIata,
  dropOffAddress,
  dropOffAirportIata,
}) =>
  Boolean(dropOffAirportIata || dropOffAddress) &&
  !pickUpAirportIata &&
  !pickUpAddress

// The mirror of the above: the caller named a pick-up airport and no drop-off one, so the
// flight LANDS at the booked airport.
//
// This matters because the provider indexes a flight by its DEPARTURE date, while an
// arrival booking is dated by when the passenger LANDS. For anything that flies overnight
// those are different days — UA385 EWR->DBV departs 9 Oct 19:05 and arrives 10 Oct 10:15 —
// so asking for the arrival date returns nothing at all. The resolver already handles this
// by retrying the previous day, but only when isPickUpContext is set, and the standalone-VIP
// SEARCH screen passes neither `type` nor airportPickUpInfo, so on that surface it never
// fired: every long-haul arrival (transatlantic, transpacific, any red-eye) reported
// "No flight found" for a flight that exists and that the departure tab finds happily.
//
// Complementary to the hook's own cross-midnight correction, not a duplicate of it: that
// one fires when a flight IS returned carrying the wrong date, this one when NOTHING is
// returned. Date reconciliation stays off for these callers via callerManagesDate.
const isArrivalOnlyLookup = ({
  pickUpAddress,
  pickUpAirportIata,
  dropOffAddress,
  dropOffAirportIata,
}) =>
  Boolean(pickUpAirportIata || pickUpAddress) &&
  !dropOffAirportIata &&
  !dropOffAddress

const FlightTracking = ({
  // 'arrival' | 'departure' — the booking's OWN direction, when the caller knows it.
  // Makes leg selection independent of which address prop carried the airport; see the
  // reconciliation in flightNerveResolver.js. Omitted by callers that do not know it,
  // which keeps today's prop-shape inference for them.
  bookingDirection,
  pickUpDate,
  dropOffDate,
  dropOffAtoB,
  searchQuery,
  flightNumber,
  airportPickUpInfo = null,
  airportDropOffInfo = null,
  // When the parent's validation resolves to a date mismatch, hide the flight
  // result card — the red "Flight date does not match selected date" message
  // already explains it, and showing the adjacent-day flight (pulled by the
  // resolver's prev-day retry for non-daily flights) is confusing. The data is
  // still emitted via onFetchFlightData so the parent can raise that error.
  hideResultsCard = false,
  // Booking pages: render a flight card only for a flight that is VERIFIED for the booking
  // by the same rule the booking form uses (AirportPickupSection / AirportDropOffSection):
  // it touches the booked airport and is on the booked day. Those forms gate the card on
  // their own verdict; the booking pages had no verdict and showed whatever the lookup
  // returned — 5625-V (pick-up 20 Oct) showed AC866's 20 Oct departure, which lands on the
  // 21st, re-dated by the card to look like a match. Off unless the caller opts in.
  verifiedFlightsOnly = false,
  // The booking's own time for this leg (pick-up, or the car's arrival at the airport for a
  // drop-off) that a flight is verified against. Passed explicitly because the lookup props
  // (pickUpDate / dropOffDate) are not always the booking's time: a drop-off without
  // tripInfo has no dropOffDate. Falls back to them when omitted.
  verificationDate,
  // Told whether a verified flight card is on screen, so the page can keep the standard
  // flight-tracking prompt to verified flights only (as the booking forms do).
  onVerifiedFlightsChange,
  onFetchFlightData = () => {},
  onFlightTrackingPickUp = () => {},
  onFlightTrackingDropOff = () => {},
  onLoadingData: onLoadingDataProp = () => {},
  setBookingLoading = () => {},
  flightTrackingPickUp,
  flightTrackingDropOff,
  type,
  locationCoord,
  callApiPickUp = true,
  callApiDropOff = true,
  pickUpCache = null,
  dropOffCache = null,
  cachePickUpInfo = () => {},
  cacheDropOffInfo = () => {},
  switchKey = false,
  afterLoadingFlight = () => {},
  pickUpAddress,
  dropOffAddress,
  // Authoritative IATA of the BOOKED airport when the caller knows it (the transfer /
  // chauffeur search sets pickUpLocation.iata / dropOffLocation.iata from the place
  // lookup). Preferred over scraping a code out of the address text.
  pickUpAirportIata,
  dropOffAirportIata,
  useDateAsIs = false,
  showSkeletonLoading = false,
  isLoading = false,
  noMarginTop = false,
  alwaysExpanded = false,
}) => {
  const [getCity, setGetCity] = useState({})
  const [departureTime, setDepartureTime] = useState({})
  const [arrivalTime, setArrivalTime] = useState({})
  const [delayDepartureTime, setDelayDepartureTime] = useState(null)
  const [delayArrivalTime, setDelayArrivalTime] = useState(null)
  const [flightInfo, setFlightInfo] = useState(null)
  const [flightTotalDuration, setFlightTotalDuration] = useState()
  const [progressPercentage, setProgressPercentage] = useState()
  // ── The spinner must never outlive the lookup ─────────────────────────────────
  // onLoadingData(true) is emitted before a lookup starts, and every exit inside this
  // component emits false again — except a lookup superseded by UNMOUNT. The five
  // `if (isStale()) return` guards return early by design (a dead instance must not write
  // state), no replacement instance arrives to clear the flag, and the PARENT keeps
  // spinning: it owns the loader on the flight-number field and outlives the card it
  // renders. That is the stuck loader on the admin add-on VIP edit form
  // (AirportPickupSection renders FlightTracking conditionally and survives it).
  //
  // Tracked, not blanket-cleared. Emitting false on every unmount would also fire when
  // nothing was loading, and would log React's "state update on an unmounted component"
  // warning whenever the parent tears down in the same pass. This fires only when THIS
  // instance genuinely left a spinner on, which by definition means someone is still
  // waiting for it.
  //
  // Declared as a wrapper over the prop so the ~16 existing onLoadingData(...) call sites
  // are unchanged — they all resolve to this.
  // Set on unmount so checkTime's post-await callback can bail instead of touching a
  // dead instance. Reset when the effect re-runs, since the same ref serves every pass.
  const cancelledRef = useRef(false)
  const loadingEmittedRef = useRef(false)
  const onLoadingDataRef = useRef(onLoadingDataProp)
  // In an effect, not inline during render: a render that React starts and throws away
  // (StrictMode's double-invoke, or anything concurrent later) must not mutate a ref.
  // Committed renders are the only ones that should update it, which is what this gives.
  useEffect(() => {
    onLoadingDataRef.current = onLoadingDataProp
  })
  const onLoadingData = value => {
    loadingEmittedRef.current = Boolean(value)
    onLoadingDataRef.current(value)
  }
  useEffect(
    () => () => {
      if (loadingEmittedRef.current) onLoadingDataRef.current(false)
    },
    []
  )

  const [flightDataLoading, setFlightDataLoading] = useState(true)
  const [noData, setNoData] = useState(false)
  const [time, setTime] = useState({})
  const [updateCache, setUpdateCache] = useState(false)
  const [oldQuery, setOldQuery] = useState('')
  const [availableFlights, setAvailableFlights] = useState([])
  const [expanded, setExpanded] = useState({})
  const [terminals, setTerminals] = useState(null)
  const requestIdRef = useRef(0)
  // Per-leg token of the latest fetch this session: { pickUp: dedupeKey, dropOff: dedupeKey }.
  // Used to drop slower old-date responses and to keep a fresh fetch authoritative
  // over a stale pickUpCache/dropOffCache restore.
  const lastFetchedFlightRef = useRef(null)

  // Resolve the three independent lookups (timezone, city names, airline
  // name) in one parallel batch instead of three serial round-trips. The
  // timezone lookup historically receives the raw IATA values while the city
  // lookup receives the normalized 3-letter codes — preserved here.
  const resolveFlightMeta = async ({
    data,
    arrival,
    departure,
    departInfo,
    arrivInfo,
    airlineNamePromise,
  }) => {
    const arrIata = arrivInfo.airportCode || arrivInfo['Airport:'] || ''
    const depIata = departInfo.airportCode || departInfo['Airport:'] || ''
    arrival.iata = arrIata
    departure.iata = depIata

    const rawArrIata = arrival.iata
    const rawDepIata = departure.iata

    const arrivalParts = arrival.iata.trim().split(/\s+/)
    if (arrivalParts[0] && /^[A-Z]{3}$/.test(arrivalParts[0])) {
      arrival.iata = arrivalParts[0]
    }
    const departureParts = departure.iata.trim().split(/\s+/)
    if (departureParts[0] && /^[A-Z]{3}$/.test(departureParts[0])) {
      departure.iata = departureParts[0]
    }

    // FlightNerve returns airport name/city/country inline (see
    // flightNerveMapper.js) — no network call needed here, unlike the old
    // getCitesNamesCached round-trip to a static-airports-JSON route.
    const isDomestic =
      Boolean(departInfo.airportCountryCode) &&
      departInfo.airportCountryCode === arrivInfo.airportCountryCode
    // Include `country` so legacy consumers (AirportPickup/DropOff flightSize)
    // keep working alongside the preferred `type` field.
    const cityNamesResult = {
      ok: true,
      names: {
        departureInfo: {
          iata_faa: rawDepIata,
          name: departInfo.airportName || '',
          city: departInfo.airportCity || '',
          country: departInfo.airportCountryCode || '',
          type: isDomestic ? 'domestic' : 'international',
        },
        arrivalInfo: {
          iata_faa: rawArrIata,
          name: arrivInfo.airportName || '',
          city: arrivInfo.airportCity || '',
          country: arrivInfo.airportCountryCode || '',
          type: isDomestic ? 'domestic' : 'international',
        },
      },
    }

    const [airlineTimeZone, airlineName] = await Promise.all([
      getAirlineTimeZoneCached(rawArrIata, rawDepIata),
      airlineNamePromise,
    ])

    if (airlineTimeZone.length) {
      const [obj] = airlineTimeZone
      arrival.timezone = obj.arrival_timeZone
      departure.timezone = obj.departure_timeZone
    }
    data.airline.name = airlineName

    return cityNamesResult
  }

  const getFlightInformation = async (
    date,
    query,
    value,
    pickUpOrDropoff,
    ctx
  ) => {
    const { isStale, signal } = ctx
    // Date-aware de-dup key: a request is unique per (flight number, date, leg), NOT
    // flight number alone — so a pick-up/drop-off DATE change with the same flight number
    // is treated as a distinct fetch and refreshes the card. Also used as a PER-LEG
    // stale-request token so a slower old-date response can't overwrite a newer one.
    // The BOOKED AIRPORTS belong in this key. They are inputs to the lookup, not
    // decoration: they become `depap` and the expectedArrival/expectedDeparture leg hints,
    // and the API's own cache key already includes all three
    // (`status:NAME:NUM:dDATE:pDEPAP:eaARR:edDEP` in flightNerve.js). Leaving them out
    // here made the client key COARSER than the server key, with two consequences:
    //
    //   1. The failed-lookup latch replayed a failure from a DIFFERENT airport. Enter
    //      AZ 1627 with BDS (wrong — it flies FCO->BDS), get "not found"; correct the
    //      airport to FCO and the key is unchanged, so the next lookup is short-circuited
    //      to "not found" with zero network for the full 30s TTL. Waiting it out and
    //      clicking Search again "fixed" it, which is exactly what a stale latch looks
    //      like from the outside.
    //   2. Changing ONLY the airport never re-fetched at all — `dedupeKey === oldQuery`
    //      falls through to the no-op branch — so a multi-leg number kept the leg it
    //      resolved against the previous airport.
    //
    // Extracted through the resolver's own extractIataCode so the key changes on a real
    // airport change and not on cosmetic address-text differences. Both sides are
    // included: the resolver sends BOTH hints regardless of leg, so either one changing
    // can change the answer.
    // Falls back to the raw hint when no code can be extracted (a Google place such as
    // LaGuardia may carry none): otherwise a lookup WITH an unreadable airport and one with NO
    // airport share a key, and the first one's failure latch blocks the second.
    const pickUpAirportHint = pickUpAirportIata || pickUpAddress || ''
    const dropOffAirportHint = dropOffAirportIata || dropOffAddress || ''
    const airportKey = `${extractIataCode(pickUpAirportHint) ||
      pickUpAirportHint}>${extractIataCode(dropOffAirportHint) ||
      dropOffAirportHint}`
    const dedupeKey = `${query}|${date}|${pickUpOrDropoff}|${airportKey}`
    if (lookupRecentlyFailed(dedupeKey)) {
      setNoData(true)
      setFlightDataLoading(false)
      onLoadingData(false)
      onFetchFlightData(null)
      return
    }
    if (value === 'today-yesterday') {
      if (dedupeKey !== oldQuery) {
        setOldQuery(dedupeKey)
        // A genuinely-new lookup is starting: clear the no-data latch and show
        // loading. Without this reset noData stays true from a prior failed
        // lookup, keeping "flight does not exist" on screen through this load and
        // suppressing a successful result. Placed here (not on every effect run)
        // so redundant re-renders with an unchanged query don't flicker loading.
        setNoData(false)
        setFlightDataLoading(true)
        // Drop the PREVIOUS flight's results before asking about this one. They are only
        // ever written on success, so without this they survive the whole next lookup —
        // and because React 16 does not batch setState in an async continuation,
        // processData() commits `getCity` in its own render BEFORE setAvailableFlights()
        // runs below. In that render checkTime() judged the new booking against the OLD
        // flight: no match, so it emitted onFetchFlightData(null) and the parent reported
        // "Flight not found" — stamped with the NEW flight number, for a flight that
        // resolves perfectly one render later. That is the DL625-after-UA1080 case.
        //
        // Safe to clear: with the list empty checkTime falls through to the branch that
        // reads the current getCity, and its desired-vs-payload flight-key guard drops a
        // payload that still belongs to the previous flight rather than emitting it.
        setAvailableFlights([])
        lastFetchedFlightRef.current = {
          ...(lastFetchedFlightRef.current || {}),
          [pickUpOrDropoff]: dedupeKey,
        }

        const { fname, fnumber, fOrigName } = parseFlightNumber(query)

        let apiDate = date.split('-').join('')

        // Stage A: the airline name only depends on the carrier code, which is
        // known before any fetch — resolve it in parallel with everything else.
        const airlineNamePromise = getAirlineNameCached(fname)
        airlineNamePromise.catch(() => {})

        // Built once so the date-reconciliation retry below can re-issue the IDENTICAL
        // lookup against a different day without the two drifting apart.
        const departureOnlyLookup = isDepartureOnlyLookup({
          pickUpAddress,
          pickUpAirportIata,
          dropOffAddress,
          dropOffAirportIata,
        })
        const arrivalOnlyLookup = isArrivalOnlyLookup({
          pickUpAddress,
          pickUpAirportIata,
          dropOffAddress,
          dropOffAirportIata,
        })
        const resolveArgs = {
          fname,
          fnumber,
          fOrigName,
          pickUpAddress,
          dropOffAddress,
          pickUpAirportIata,
          dropOffAirportIata,
          bookingDirection,
          pickUpDate,
          isPickUpContext:
            type === 'pickUp' ||
            arrivalOnlyLookup ||
            (airportPickUpInfo != null && !departureOnlyLookup),
          // useDateAsIs marks the callers that own their own cross-midnight correction
          // (the standalone-VIP surfaces). Tell the resolver so it does not ALSO shift
          // the date and fight them; only the guess-based callers want reconciliation.
          callerManagesDate: useDateAsIs,
          shouldRetryPrevDayOnCancelled:
            type === 'pickUp' ||
            (!departureOnlyLookup &&
              (airportPickUpInfo != null || Boolean(pickUpAddress))),
          // The controller this effect already creates — now actually wired to the
          // network, so replacing a lookup cancels it instead of merely ignoring it.
          signal,
        }

        let newApiInfo = await resolveFlightNerveInfo({
          ...resolveArgs,
          apiDate,
        })
        if (isStale()) return
        // The booked side is the one the agent typed a date against: an ARRIVAL booking is
        // dated by when the flight lands, a DEPARTURE by when it takes off.
        newApiInfo = await reconcileBookedDate({
          legs: newApiInfo,
          apiDate,
          bookedDay: date,
          // `pickUpOrDropoff` says which DATE PROP drove this lookup (see flightCode:
          // `if (pickUpDate) -> 'pickUp'`), NOT what kind of booking it is. The
          // standalone-VIP DEPARTURE surfaces have their own "pick up date/time" — the
          // greeter meets the passenger BEFORE the flight leaves — so they drive the
          // lookup from pickUpDate and were classified 'pickUp', telling this reconciler
          // the booked day was the ARRIVAL day. For UA844 GRU->ORD booked 17 Aug it then
          // hunted the leg LANDING on the 17th, found the 16 Aug departure (dep 16th
          // 22:20 -> arr 17th 06:45) and replaced a correct `confirmed` 17 Aug result
          // with a `projected` 16 Aug one. Validation then rejected a flight we had
          // picked ourselves, making every overnight departure unbookable.
          //
          // A caller that names a DROP-OFF airport and no pick-up airport is booking a
          // departure, whatever date prop it used — so that case is answered first and
          // everything else keeps its existing answer verbatim. Deliberately narrow:
          // callers passing BOTH addresses (the admin VIP edit and booking-details
          // cards, which render an arrival and a departure leg from one component) fall
          // through untouched, as do all the transfer/add-on surfaces.
          side:
            dropOffAddress && !pickUpAddress
              ? 'departure'
              : pickUpOrDropoff === 'pickUp'
              ? 'arrival'
              : 'departure',
          resolve: shiftedApiDate =>
            resolveFlightNerveInfo({ ...resolveArgs, apiDate: shiftedApiDate }),
        })
        if (isStale()) return
        if (!newApiInfo) {
          // Peek (non-draining) at the failure buffer BEFORE onFetchFlightData(null)
          // hands the turn to the parent's Slack reporter, whose describe call drains it.
          markLookupFailed(dedupeKey, {
            transient: hadRecentTransientFlightNerveFailure(query),
          })
          setNoData(true)
          setFlightDataLoading(false)
          onLoadingData(false)
          // Signal the consumer so it stops the "searching" spinner and shows a
          // clean "flight not found" — without this the caller's isFetchingFlight
          // stays true and the Search button hangs.
          onFetchFlightData(null)
          return
        }

        if (newApiInfo && !newApiInfo.message && newApiInfo.length) {
          const [departureInfo, arrivalInfo] = newApiInfo

          const results = []
          let isCancelledFlight = false
          if (departureInfo.departure) {
            const data = {
              departure: {},
              arrival: {},
            }
            setTerminals({
              arrival: arrivalInfo.arrival.terminal,
              departure: departureInfo.departure.terminal,
            })
            data.departure.terminal = departureInfo.departure.terminal || null
            data.departure.gate = departureInfo.departure.gate || null
            data.arrival.terminal = arrivalInfo.arrival.terminal || null
            data.arrival.gate = arrivalInfo.arrival.gate || null
            data.airline = {
              iata: fname.toUpperCase(),
              number: fnumber,
            }
            const { arrival, departure } = data

            const departInfo = departureInfo.departure
            const arrivInfo = arrivalInfo.arrival

            data.flight_status = departInfo['Status:']

            if (CANCELLED_STATUSES.includes(data.flight_status)) {
              isCancelledFlight = true
            }

            // RD-4144: how far the provider's estimate is off the schedule. Kept apart from
            // estimated/scheduled, which stay the schedule for the matching and day logic.
            const offsets = estimateOffsets(departInfo, arrivInfo, apiDate)
            departure.offsetMinutes = offsets.departure
            arrival.offsetMinutes = offsets.arrival
            departure.delay = offsets.departure > 0 ? offsets.departure : null
            arrival.delay = offsets.arrival > 0 ? offsets.arrival : null
            // What the card shows: the estimate only when it is late, which is when its label
            // reads ESTIMATED. An early estimate stays the schedule under "SCHEDULED".
            departure.shownOffset = departure.delay || 0
            arrival.shownOffset = arrival.delay || 0

            const {
              departureTimeDate,
              arrivalTimeDate,
            } = resolveFlightDateTimes(
              departInfo['scheduledTime'],
              arrivInfo['scheduledTime'],
              apiDate
            )

            arrival.estimated = arrivalTimeDate
            arrival.scheduled = arrivalTimeDate

            departure.estimated = departureTimeDate
            departure.scheduled = departureTimeDate

            // Absolute instants, used ONLY for the duration — never rendered. The local
            // values above are reconstructed from the provider's "HH:mm, Mon DD" strings,
            // which carry no offset, so subtracting them is only correct when both
            // airports share a timezone. See getFlightDuration in ./helpers.
            //
            // ONLY the genuine `*DateTimeUtc` field, never a fallback to the offset-bearing
            // ISO string. That fallback used to be safe because FlightNerve's `*DateTime`
            // parsed to the right instant even for DOH/KWI/IST, where it stamps +00:00
            // instead of the real offset but the VALUE is genuinely UTC.
            //
            // flightapi.io breaks that assumption for every airport, not a rare few: it
            // sends no `*DateTimeUtc` field at all, and its `*DateTime` labels the LOCAL
            // wall clock with a fake -00:00 offset — the offset is fiction, the value is
            // not UTC. Falling back to it here fed a local time straight into a genuine UTC
            // diff. Reproduced on SQ607 ICN->SIN: departure "09:00" and arrival "14:20"
            // (both local) diffed as if both were UTC gave 5h20m; the flight is 6h20m
            // (ICN +09:00, SIN +08:00 — a real 1-hour offset the naive diff cannot see).
            // The same fallback also fed FlightTracking.jsx's localWallClockFromUtc
            // reconversion below, which is the sharper failure: it shifted the DISPLAYED
            // time forward by each airport's own offset — 09:00 became 18:00, 14:20 became
            // 22:20, both wrong by exactly the local UTC offset.
            //
            // With the fallback gone, a leg with no genuine UTC field leaves `.utc` null:
            // localWallClockFromUtc(null, tz) returns null (see helpers.js), so the display
            // time set above from the local "HH:mm, Mon DD" string is left untouched, and
            // getFlightDuration's own per-airport-timezone branch takes over for duration —
            // which is what actually produces the correct 6h20m, not a naive UTC diff.
            arrival.utc = arrivInfo.arrivalDateTimeUtc || null
            departure.utc = departInfo.departureDateTimeUtc || null

            // No `signal` here, deliberately. resolveFlightMeta's two remaining calls
            // (airline name, airport timezones) go through the shared per-key promise cache
            // in apiCache.js, so aborting one consumer's request would poison the cached
            // entry for every other consumer waiting on it. They are cached per IATA and
            // only run once a flight has already resolved, so the un-abortable cost is
            // bounded — unlike the lookup ladder, which is what the signal exists for.
            // Passing it here previously did nothing at all: it was never destructured.
            const cityNamesResult = await resolveFlightMeta({
              data,
              arrival,
              departure,
              departInfo,
              arrivInfo,
              airlineNamePromise,
              query,
            })
            if (isStale()) return

            // resolveFlightMeta has just filled in the timezones (FlightNerve's own, or
            // TimeZoneDB by position when it has none). Now that the zone is known, rebuild
            // the local wall-clock from the true instant rather than trusting the string
            // FlightNerve labelled "local" — for DOH/KWI/IST that string is actually UTC.
            // No-op for every airport they got right; see localWallClockFromUtc.
            const arrivalLocal = localWallClockFromUtc(
              arrival.utc,
              arrival.timezone
            )
            if (arrivalLocal) {
              arrival.estimated = arrivalLocal
              arrival.scheduled = arrivalLocal
            }
            const departureLocal = localWallClockFromUtc(
              departure.utc,
              departure.timezone
            )
            if (departureLocal) {
              departure.estimated = departureLocal
              departure.scheduled = departureLocal
            }

            const processedData = processData(
              data,
              arrival,
              departure,
              value,
              pickUpOrDropoff,
              cityNamesResult
            )

            setExpanded(exisitingValues => ({
              ...exisitingValues,
            }))
            if (processedData && !isCancelledFlight) results.push(processedData)
          }

          let filteredResults = []
          if (results.length && pickUpAddress) {
            const matchingFlight = results.find(item =>
              addressMatchesFlightAirportInfo(
                pickUpAddress,
                item.getCity?.arrivalInfo
              )
            )

            filteredResults = matchingFlight ? [matchingFlight] : results
          } else if (results.length && dropOffAddress) {
            const matchingFlight = results.find(item =>
              addressMatchesFlightAirportInfo(
                dropOffAddress,
                item.getCity?.departureInfo
              )
            )

            filteredResults = matchingFlight ? [matchingFlight] : results
          } else {
            filteredResults = results
          }

          // Drop this response if a newer request (e.g. a later date change) for the same
          // leg has since started — otherwise a slower old-date response overwrites it.
          if (
            lastFetchedFlightRef.current &&
            lastFetchedFlightRef.current[pickUpOrDropoff] !== dedupeKey
          )
            return
          markLookupSucceeded(dedupeKey)
          setAvailableFlights(filteredResults)
          setBookingLoading(false)
        } else {
          setNoData(true)
          onLoadingData(false)
          if (pickUpOrDropoff === 'pickUp') {
            cachePickUpInfo(null)
          } else {
            cacheDropOffInfo(null)
          }
          onFetchFlightData(null)
          setUpdateCache(false)
          setFlightDataLoading(false)
          setBookingLoading(false)
          afterLoadingFlight()
        }
      } else {
        onLoadingData(false)
        setFlightDataLoading(false)
      }
    } else {
      if (dedupeKey !== oldQuery) {
        setOldQuery(dedupeKey)
        // See the today-yesterday branch: clear the no-data latch + show loading
        // when a genuinely-new lookup starts, so a prior failure can't keep
        // "flight does not exist" pinned through this fetch.
        setNoData(false)
        setFlightDataLoading(true)
        // Drop the PREVIOUS flight's results before asking about this one. They are only
        // ever written on success, so without this they survive the whole next lookup —
        // and because React 16 does not batch setState in an async continuation,
        // processData() commits `getCity` in its own render BEFORE setAvailableFlights()
        // runs below. In that render checkTime() judged the new booking against the OLD
        // flight: no match, so it emitted onFetchFlightData(null) and the parent reported
        // "Flight not found" — stamped with the NEW flight number, for a flight that
        // resolves perfectly one render later. That is the DL625-after-UA1080 case.
        //
        // Safe to clear: with the list empty checkTime falls through to the branch that
        // reads the current getCity, and its desired-vs-payload flight-key guard drops a
        // payload that still belongs to the previous flight rather than emitting it.
        setAvailableFlights([])
        lastFetchedFlightRef.current = {
          ...(lastFetchedFlightRef.current || {}),
          [pickUpOrDropoff]: dedupeKey,
        }

        const { fname, fnumber, fOrigName } = parseFlightNumber(query)

        let apiDate = null
        if (useDateAsIs) {
          apiDate = date.split('-').join('')
        } else if (pickUpOrDropoff === 'pickUp') {
          let newDate = moment.utc(pickUpDate)
          let hours = newDate.hours()
          if (hours <= 3) {
            const pickUp = moment.utc(pickUpDate)
            const newDate = pickUp.subtract(1, 'days').format('YYYY-MM-DD')
            apiDate = newDate.split('-').join('')
          } else {
            apiDate = date.split('-').join('')
          }
        } else {
          apiDate = date.split('-').join('')
        }

        // Stage A: resolve the airline name in parallel with everything else.
        const airlineNamePromise = getAirlineNameCached(fname)
        airlineNamePromise.catch(() => {})

        // Built once so the date-reconciliation retry below can re-issue the IDENTICAL
        // lookup against a different day without the two drifting apart.
        const resolveArgs = {
          fname,
          fnumber,
          fOrigName,
          pickUpAddress,
          dropOffAddress,
          pickUpAirportIata,
          dropOffAirportIata,
          bookingDirection,
          pickUpDate,
          isPickUpContext:
            type === 'pickUp' ||
            isArrivalOnlyLookup({
              pickUpAddress,
              pickUpAirportIata,
              dropOffAddress,
              dropOffAirportIata,
            }) ||
            (airportPickUpInfo != null &&
              !isDepartureOnlyLookup({
                pickUpAddress,
                pickUpAirportIata,
                dropOffAddress,
                dropOffAirportIata,
              })),
          // useDateAsIs marks the callers that own their own cross-midnight correction
          // (the standalone-VIP surfaces). Tell the resolver so it does not ALSO shift
          // the date and fight them; only the guess-based callers want reconciliation.
          callerManagesDate: useDateAsIs,
          shouldRetryPrevDayOnCancelled: false,
          signal,
        }

        let newApiInfo = await resolveFlightNerveInfo({
          ...resolveArgs,
          apiDate,
        })
        if (isStale()) return
        // The booked side is the one the agent typed a date against: an ARRIVAL booking is
        // dated by when the flight lands, a DEPARTURE by when it takes off.
        newApiInfo = await reconcileBookedDate({
          legs: newApiInfo,
          apiDate,
          bookedDay: date,
          // `pickUpOrDropoff` says which DATE PROP drove this lookup (see flightCode:
          // `if (pickUpDate) -> 'pickUp'`), NOT what kind of booking it is. The
          // standalone-VIP DEPARTURE surfaces have their own "pick up date/time" — the
          // greeter meets the passenger BEFORE the flight leaves — so they drive the
          // lookup from pickUpDate and were classified 'pickUp', telling this reconciler
          // the booked day was the ARRIVAL day. For UA844 GRU->ORD booked 17 Aug it then
          // hunted the leg LANDING on the 17th, found the 16 Aug departure (dep 16th
          // 22:20 -> arr 17th 06:45) and replaced a correct `confirmed` 17 Aug result
          // with a `projected` 16 Aug one. Validation then rejected a flight we had
          // picked ourselves, making every overnight departure unbookable.
          //
          // A caller that names a DROP-OFF airport and no pick-up airport is booking a
          // departure, whatever date prop it used — so that case is answered first and
          // everything else keeps its existing answer verbatim. Deliberately narrow:
          // callers passing BOTH addresses (the admin VIP edit and booking-details
          // cards, which render an arrival and a departure leg from one component) fall
          // through untouched, as do all the transfer/add-on surfaces.
          side:
            dropOffAddress && !pickUpAddress
              ? 'departure'
              : pickUpOrDropoff === 'pickUp'
              ? 'arrival'
              : 'departure',
          resolve: shiftedApiDate =>
            resolveFlightNerveInfo({ ...resolveArgs, apiDate: shiftedApiDate }),
        })
        if (isStale()) return
        if (!newApiInfo) {
          // Peek (non-draining) at the failure buffer BEFORE onFetchFlightData(null)
          // hands the turn to the parent's Slack reporter, whose describe call drains it.
          markLookupFailed(dedupeKey, {
            transient: hadRecentTransientFlightNerveFailure(query),
          })
          setNoData(true)
          setFlightDataLoading(false)
          onLoadingData(false)
          // Signal the consumer so it stops the "searching" spinner and shows a
          // clean "flight not found" — without this the caller's isFetchingFlight
          // stays true and the Search button hangs.
          onFetchFlightData(null)
          return
        }

        if (newApiInfo && newApiInfo.length) {
          const [departureInfo, arrivalInfo] = newApiInfo

          const results = []
          let isCancelledFlight = false
          if (departureInfo.departure) {
            const data = {
              departure: {},
              arrival: {},
            }
            data.flight_status = 'scheduled'
            data.departure.delay = null
            data.departure.terminal = departureInfo.departure.terminal || null
            data.departure.gate = departureInfo.departure.gate || null
            data.arrival.delay = null
            data.arrival.terminal = arrivalInfo.arrival.terminal || null
            data.arrival.gate = arrivalInfo.arrival.gate || null
            setTerminals({
              arrival: arrivalInfo.arrival.terminal,
              departure: departureInfo.departure.terminal,
            })
            data.airline = {
              iata: fname.toUpperCase(),
              number: fnumber,
            }
            const { arrival, departure } = data

            const departInfo = departureInfo.departure
            const arrivInfo = arrivalInfo.arrival

            if (CANCELLED_STATUSES.includes(departInfo['Status:'] ?? '')) {
              isCancelledFlight = true
            }

            const {
              departureTimeDate,
              arrivalTimeDate,
            } = resolveFlightDateTimes(
              departInfo['scheduledTime'],
              arrivInfo['scheduledTime'],
              apiDate
            )

            arrival.estimated = arrivalTimeDate
            arrival.scheduled = arrivalTimeDate

            departure.estimated = departureTimeDate
            departure.scheduled = departureTimeDate

            // Absolute instants, used ONLY for the duration — never rendered. The local
            // values above are reconstructed from the provider's "HH:mm, Mon DD" strings,
            // which carry no offset, so subtracting them is only correct when both
            // airports share a timezone. See getFlightDuration in ./helpers.
            //
            // ONLY the genuine `*DateTimeUtc` field, never a fallback to the offset-bearing
            // ISO string. That fallback used to be safe because FlightNerve's `*DateTime`
            // parsed to the right instant even for DOH/KWI/IST, where it stamps +00:00
            // instead of the real offset but the VALUE is genuinely UTC.
            //
            // flightapi.io breaks that assumption for every airport, not a rare few: it
            // sends no `*DateTimeUtc` field at all, and its `*DateTime` labels the LOCAL
            // wall clock with a fake -00:00 offset — the offset is fiction, the value is
            // not UTC. Falling back to it here fed a local time straight into a genuine UTC
            // diff. Reproduced on SQ607 ICN->SIN: departure "09:00" and arrival "14:20"
            // (both local) diffed as if both were UTC gave 5h20m; the flight is 6h20m
            // (ICN +09:00, SIN +08:00 — a real 1-hour offset the naive diff cannot see).
            // The same fallback also fed FlightTracking.jsx's localWallClockFromUtc
            // reconversion below, which is the sharper failure: it shifted the DISPLAYED
            // time forward by each airport's own offset — 09:00 became 18:00, 14:20 became
            // 22:20, both wrong by exactly the local UTC offset.
            //
            // With the fallback gone, a leg with no genuine UTC field leaves `.utc` null:
            // localWallClockFromUtc(null, tz) returns null (see helpers.js), so the display
            // time set above from the local "HH:mm, Mon DD" string is left untouched, and
            // getFlightDuration's own per-airport-timezone branch takes over for duration —
            // which is what actually produces the correct 6h20m, not a naive UTC diff.
            arrival.utc = arrivInfo.arrivalDateTimeUtc || null
            departure.utc = departInfo.departureDateTimeUtc || null

            // No `signal` here, deliberately. resolveFlightMeta's two remaining calls
            // (airline name, airport timezones) go through the shared per-key promise cache
            // in apiCache.js, so aborting one consumer's request would poison the cached
            // entry for every other consumer waiting on it. They are cached per IATA and
            // only run once a flight has already resolved, so the un-abortable cost is
            // bounded — unlike the lookup ladder, which is what the signal exists for.
            // Passing it here previously did nothing at all: it was never destructured.
            const cityNamesResult = await resolveFlightMeta({
              data,
              arrival,
              departure,
              departInfo,
              arrivInfo,
              airlineNamePromise,
              query,
            })
            if (isStale()) return

            // resolveFlightMeta has just filled in the timezones (FlightNerve's own, or
            // TimeZoneDB by position when it has none). Now that the zone is known, rebuild
            // the local wall-clock from the true instant rather than trusting the string
            // FlightNerve labelled "local" — for DOH/KWI/IST that string is actually UTC.
            // No-op for every airport they got right; see localWallClockFromUtc.
            const arrivalLocal = localWallClockFromUtc(
              arrival.utc,
              arrival.timezone
            )
            if (arrivalLocal) {
              arrival.estimated = arrivalLocal
              arrival.scheduled = arrivalLocal
            }
            const departureLocal = localWallClockFromUtc(
              departure.utc,
              departure.timezone
            )
            if (departureLocal) {
              departure.estimated = departureLocal
              departure.scheduled = departureLocal
            }

            const processedData = processData(
              data,
              arrival,
              departure,
              value,
              pickUpOrDropoff,
              cityNamesResult
            )
            setExpanded(exisitingValues => ({
              ...exisitingValues,
            }))
            if (processedData && !isCancelledFlight) results.push(processedData)
          }

          let filteredResults = []
          if (results.length && pickUpAddress) {
            const matchingFlight = results.find(item =>
              addressMatchesFlightAirportInfo(
                pickUpAddress,
                item.getCity?.arrivalInfo
              )
            )

            filteredResults = matchingFlight ? [matchingFlight] : results
          } else if (results.length && dropOffAddress) {
            const matchingFlight = results.find(item =>
              addressMatchesFlightAirportInfo(
                dropOffAddress,
                item.getCity?.departureInfo
              )
            )

            filteredResults = matchingFlight ? [matchingFlight] : results
          } else {
            filteredResults = results
          }
          // Drop this response if a newer request (e.g. a later date change) for the same
          // leg has since started — otherwise a slower old-date response overwrites it.
          if (
            lastFetchedFlightRef.current &&
            lastFetchedFlightRef.current[pickUpOrDropoff] !== dedupeKey
          )
            return
          markLookupSucceeded(dedupeKey)
          setAvailableFlights(filteredResults)
          setBookingLoading(false)
        } else {
          setNoData(true)
          onLoadingData(false)
          if (pickUpOrDropoff === 'pickUp') {
            cachePickUpInfo(null)
          } else {
            cacheDropOffInfo(null)
          }
          onFetchFlightData(null)
          setUpdateCache(false)
          setFlightDataLoading(false)
          setBookingLoading(false)
          afterLoadingFlight()
        }
      } else {
        onLoadingData(false)
        setFlightDataLoading(false)
      }
    }
  }

  const processData = (
    data,
    arrival,
    departure,
    value,
    pickUpOrDropoff,
    cityNamesResult
  ) => {
    setFlightDataLoading(true)
    setBookingLoading(true)
    setFlightInfo(data)

    if (cityNamesResult.ok) {
      setGetCity(cityNamesResult.names)
    }
    const cityNames = cityNamesResult.names

    if (!cityNames || !cityNames.departureInfo || !cityNames.arrivalInfo) {
      onLoadingData(false)
      setFlightDataLoading(false)
      setBookingLoading(false)
      return null
    }

    if (value === 'today-yesterday') {
      //Delay-Time — set on every lookup, so a previous flight's delay never outlives it.
      setDelayArrivalTime(arrival.delay || null)
      setDelayDepartureTime(departure.delay || null)

      if (arrival.actual) {
        if (!useDateAsIs && pickUpOrDropoff === 'pickUp') {
          let date1 = new Date(pickUpDate)
          let date2 = new Date(arrival.actual)
          let date3 = new Date(departure.actual)
          let isSameDay = date1.getDate() === date2.getDate()
          if (!isSameDay) {
            date2.setDate(date2.getDate() - 1)
            arrival.actual = moment.utc(date2).format()

            date3.setDate(date3.getDate() - 1)
            departure.actual = moment.utc(date3).format()
          }
        }

        setProgressPercentage(
          getProgressTime(arrival.actual, departure.actual, data.flight_status)
        )
        setArrivalTime(getFormattedTime(arrival.actual))
        setDepartureTime(getFormattedTime(departure.actual))
        setFlightTotalDuration(
          getFlightDuration(
            {
              time: arrival.actual,
              timezone: arrival.timezone,
              utc: arrival.utc,
            },
            {
              time: departure.actual,
              timezone: departure.timezone,
              utc: departure.utc,
            }
          )
        )
      } else if (departure.actual) {
        if (!useDateAsIs && pickUpOrDropoff === 'pickUp') {
          let date1 = new Date(pickUpDate)
          let date2 = new Date(arrival.estimated)
          let date3 = new Date(departure.actual)
          let isSameDay = date1.getDate() === date2.getDate()
          if (!isSameDay) {
            date2.setDate(date2.getDate() - 1)
            arrival.estimated = moment.utc(date2).format()

            date3.setDate(date3.getDate() - 1)
            departure.actual = moment.utc(date3).format()
          }
        }

        setProgressPercentage(
          getProgressTime(
            arrival.estimated,
            departure.actual,
            data.flight_status
          )
        )
        setArrivalTime(
          getFormattedTime(withOffset(arrival.estimated, arrival.shownOffset))
        )
        setDepartureTime(getFormattedTime(departure.actual))
        setFlightTotalDuration(getCurrentDuration(departure.actual))
      } else {
        if (!useDateAsIs && pickUpOrDropoff === 'pickUp') {
          let date1 = new Date(pickUpDate)
          let date2 = new Date(arrival.estimated)
          let date3 = new Date(departure.estimated)
          let isSameDay = date1.getDate() === date2.getDate()
          if (!isSameDay) {
            date2.setDate(date2.getDate() - 1)
            arrival.estimated = moment.utc(date2).format()

            date3.setDate(date3.getDate() - 1)
            departure.estimated = moment.utc(date3).format()
          }
        }
        setProgressPercentage(
          getProgressTime(
            arrival.estimated,
            departure.estimated,
            data.flight_status
          )
        )
        // RD-4144: the card shows the provider's estimate (BA309: sched 15:00, est 19:24).
        setArrivalTime(
          getFormattedTime(withOffset(arrival.estimated, arrival.shownOffset))
        )
        setDepartureTime(
          getFormattedTime(
            withOffset(departure.estimated, departure.shownOffset)
          )
        )
        setFlightTotalDuration(
          getFlightDuration(
            {
              time: arrival.estimated,
              timezone: arrival.timezone,
              utc: arrival.utc,
            },
            {
              time: departure.estimated,
              timezone: departure.timezone,
              utc: departure.utc,
            }
          )
        )
      }
      onLoadingData(false)
      setUpdateCache(true)
      setFlightDataLoading(false)
      setBookingLoading(false)
      afterLoadingFlight()
      return {
        getCity: cityNames,
        // From this lookup, not the state: setState has not applied yet here.
        delayArrivalTime: arrival.delay || null,
        delayDepartureTime: departure.delay || null,
        arrivalTime: getFormattedTime(
          withOffset(arrival.estimated, arrival.shownOffset)
        ),
        departureTime: getFormattedTime(
          withOffset(departure.estimated, departure.shownOffset)
        ),
        flightInfo: data,
        flightTotalDuration: getFlightDuration(
          {
            time: arrival.estimated,
            timezone: arrival.timezone,
            utc: arrival.utc,
          },
          {
            time: departure.estimated,
            timezone: departure.timezone,
            utc: departure.utc,
          }
        ),
        progressPercentage: getProgressTime(
          arrival.estimated,
          departure.estimated,
          data.flight_status
        ),
        flightDataLoading: false,
        time: {
          arrival: data.arrival.estimated,
          departure: data.departure.estimated,
          // RD-4144: the provider's estimated landing; `arrival` stays the schedule, which
          // verification and the booked-day checks are built on.
          arrivalEstimate: sameDayEstimate(
            data.arrival.estimated,
            data.arrival.offsetMinutes
          ),
        },
      }
    } else {
      //to calculate the progress time for furture planes it will always be zero
      //set it to .1 because the lenght of a line cant be zero
      setProgressPercentage(0.1)

      //Future Flights
      let dayDiff
      if (useDateAsIs) {
        dayDiff = 0
      } else if (pickUpDate) {
        const pickDate = moment.utc(pickUpDate)
        const arrivalObjectDate = moment
          .utc(arrival.estimated)
          .format('YYYY-MM-DD')
        dayDiff = pickDate.diff(arrivalObjectDate, 'days')
      } else if (dropOffAtoB) {
        const dropAtoBDate = moment.utc(dropOffAtoB)
        const arrivalObjectDate = moment
          .utc(departure.estimated)
          .format('YYYY-MM-DD')

        dayDiff = dropAtoBDate.diff(arrivalObjectDate, 'days')
      } else if (dropOffDate) {
        const dropDate = moment(dropOffDate)
        const arrivalObjectDate = moment
          .utc(departure.estimated)
          .format('YYYY-MM-DD')
        dayDiff = dropDate.diff(arrivalObjectDate, 'days')
      }

      //this is for issue that arrival are not on the same day
      if (!useDateAsIs && pickUpOrDropoff === 'pickUp') {
        let date1 = new Date(pickUpDate)
        let date2 = new Date(arrival.scheduled)
        let isSameDay = date1.getDate() === date2.getDate()
        if (!isSameDay) {
          date2.setDate(date2.getDate() - 1)
          arrival.scheduled = moment.utc(date2).format()
        }
      }

      setFlightTotalDuration(
        getFlightDuration(
          {
            time: arrival.estimated,
            timezone: arrival.timezone,
            utc: arrival.utc,
          },
          {
            time: departure.estimated,
            timezone: departure.timezone,
            utc: departure.utc,
          }
        )
      )

      const arrivalScheduled = moment
        .utc(arrival.scheduled)
        .add(dayDiff, 'days')
      setArrivalTime(getFormattedTime(arrivalScheduled))

      const flightDuration = getTimeDifference(
        arrival.scheduled,
        departure.scheduled
      )
      const durationHours = moment.utc(flightDuration).format('HH:mm:ss')
      const arrivalTimeMoment = moment.utc(arrivalScheduled)
      const timeDifferenceMoment = moment.duration(durationHours)
      const diff = arrivalTimeMoment.subtract(timeDifferenceMoment)
      const depatrueDateTime = getFormattedTime(diff)
      setDepartureTime(depatrueDateTime)
      onLoadingData(false)
      setUpdateCache(true)
      setFlightDataLoading(false)
      setBookingLoading(false)
      afterLoadingFlight()

      return {
        getCity: cityNames,
        delayArrivalTime: delayArrivalTime,
        delayDepartureTime: delayDepartureTime,
        arrivalTime: getFormattedTime(arrivalScheduled),
        departureTime: depatrueDateTime,
        flightInfo: data,
        flightTotalDuration: getFlightDuration(
          {
            time: arrival.estimated,
            timezone: arrival.timezone,
            utc: arrival.utc,
          },
          {
            time: departure.estimated,
            timezone: departure.timezone,
            utc: departure.utc,
          }
        ),
        progressPercentage: 0.1,
        flightDataLoading: false,
        time: {
          arrival: data.arrival.estimated,
          departure: data.departure.estimated,
        },
      }
    }
  }

  useEffect(() => {
    if (!isEmpty(getCity) && !isEmpty(flightInfo)) {
      setTime({
        arrival: flightInfo.arrival.estimated,
        departure: flightInfo.departure.estimated,
      })
    }
  }, [getCity, flightInfo])

  useEffect(() => {
    cancelledRef.current = false
    const checkTime = async () => {
      let value

      if (pickUpDate) {
        value = await occurrenceDate(pickUpDate)
      } else if (dropOffAtoB) {
        value = await occurrenceDate(dropOffAtoB)
      } else if (dropOffDate) {
        value = await occurrenceDate(dropOffDate)
      }

      const date =
        value !== 'today-yesterday'
          ? getFutureDate(pickUpDate, dropOffDate, dropOffAtoB, flightInfo)
          : null

      if (availableFlights.length && pickUpAddress) {
        const matchingFlight = availableFlights.find(item =>
          addressMatchesFlightAirportInfo(
            pickUpAddress,
            item.getCity?.arrivalInfo
          )
        )

        if (!matchingFlight) {
          // This no-match exit is a FAILURE for latching purposes: without the
          // mark, a payload that resolves but can't match the booked airport
          // cycles through submit -> search -> here forever, invisibly to the
          // resolver-level latch (the LA2218 success-path loop).
          markLookupFailed(lastFetchedFlightRef.current?.pickUp)
          onFetchFlightData(null)
          return
        }

        const desiredPickUpFlightKey = normFlightQueryKey(
          searchQuery || flightNumber || ''
        )
        const payloadPickUpFlightKey = flightKeyFromProcessedFlightItem(
          matchingFlight
        )
        if (
          desiredPickUpFlightKey &&
          payloadPickUpFlightKey &&
          payloadPickUpFlightKey !== desiredPickUpFlightKey
        ) {
          return
        }

        // A confirmed match settles this leg — release any latch on its key.
        // Covers the "user fixed the ADDRESS, not the flight" flow: the earlier
        // no-match latched the key, the address change re-runs only this effect
        // (addresses are not deps of the search effect), and without this clear
        // a remount re-search within the TTL would report no-data for a pairing
        // that demonstrably matches. Placed AFTER the stale-payload guard above
        // so a match against a PREVIOUS flight's leftovers can't clear the
        // current key. Loop-safe: this path emits good data, which settles the
        // parent — the re-fire driver was onFetchFlightData(null), never this.
        markLookupSucceeded(lastFetchedFlightRef.current?.pickUp)
        onFetchFlightData({
          data: matchingFlight.getCity,
          time: matchingFlight.time,
          date: date,
          terminals: terminals,
          // Additive. The local strings in `time` carry FlightNerve's offset,
          // which is wrong for airports it has no timezone for (DOH/KWI/IST) and
          // can be day-shifted by the pick-up normalisation above. `.utc` is
          // untouched by both, so duration arithmetic must prefer it.
          utc: {
            arrival: matchingFlight.flightInfo?.arrival?.utc || null,
            departure: matchingFlight.flightInfo?.departure?.utc || null,
          },
          timezone: {
            arrival: matchingFlight.flightInfo?.arrival?.timezone || null,
            departure: matchingFlight.flightInfo?.departure?.timezone || null,
          },
        })
      } else if (availableFlights.length && dropOffAddress) {
        const matchingFlight = availableFlights.find(item =>
          addressMatchesFlightAirportInfo(
            dropOffAddress,
            item.getCity?.departureInfo
          )
        )

        if (!matchingFlight) {
          // Same latch-on-no-match as the pick-up branch above.
          markLookupFailed(lastFetchedFlightRef.current?.dropOff)
          onFetchFlightData(null)
          return
        }

        const desiredDropOffFlightKey = normFlightQueryKey(
          searchQuery || flightNumber || ''
        )
        const payloadDropOffFlightKey = flightKeyFromProcessedFlightItem(
          matchingFlight
        )
        if (
          desiredDropOffFlightKey &&
          payloadDropOffFlightKey &&
          payloadDropOffFlightKey !== desiredDropOffFlightKey
        ) {
          return
        }

        // Same match-settles-the-leg latch release as the pick-up branch above.
        markLookupSucceeded(lastFetchedFlightRef.current?.dropOff)
        onFetchFlightData({
          data: matchingFlight.getCity,
          time: matchingFlight.time,
          date: date,
          terminals: terminals,
          // Additive. The local strings in `time` carry FlightNerve's offset,
          // which is wrong for airports it has no timezone for (DOH/KWI/IST) and
          // can be day-shifted by the pick-up normalisation above. `.utc` is
          // untouched by both, so duration arithmetic must prefer it.
          utc: {
            arrival: matchingFlight.flightInfo?.arrival?.utc || null,
            departure: matchingFlight.flightInfo?.departure?.utc || null,
          },
          timezone: {
            arrival: matchingFlight.flightInfo?.arrival?.timezone || null,
            departure: matchingFlight.flightInfo?.departure?.timezone || null,
          },
        })
      } else {
        const desiredFlightKey = normFlightQueryKey(
          searchQuery || flightNumber || ''
        )
        const payloadFlightKey = flightKeyFromFlightInfoState(flightInfo)
        if (
          desiredFlightKey &&
          payloadFlightKey &&
          payloadFlightKey !== desiredFlightKey
        ) {
          return
        }

        if (cancelledRef.current) return
        onFetchFlightData({
          data: getCity,
          time: time,
          date: date,
          terminals: terminals,
          utc: {
            arrival: flightInfo?.arrival?.utc || null,
            departure: flightInfo?.departure?.utc || null,
          },
          timezone: {
            arrival: flightInfo?.arrival?.timezone || null,
            departure: flightInfo?.departure?.timezone || null,
          },
        })
      }
    }

    if (!isEmpty(time) && !isEmpty(getCity)) checkTime()
    // checkTime awaits occurrenceDate before it calls back into the parent. Without this
    // an unmount mid-await still fired onFetchFlightData on a dead instance, which React
    // 16 reports as "state update on an unmounted component" in the PARENT. The sibling
    // fetch effect below already guards this way (isStale + abort); this one predates it.
    return () => {
      cancelledRef.current = true
    }
  }, [
    availableFlights,
    time,
    getCity,
    pickUpDate,
    dropOffDate,
    dropOffAtoB,
    pickUpAddress,
    dropOffAddress,
    terminals,
    searchQuery,
    flightNumber,
    flightInfo,
  ])

  useEffect(() => {
    // Every effect run invalidates the previous one: the signal below is passed all the
    // way to fetch(), so in-flight requests are genuinely aborted, and any response that
    // still lands is dropped by the isStale() guard. Until the signal was threaded
    // through, only the second half of that was true — the requests ran to completion and
    // we merely ignored them, which is how one flight number produced several paid calls
    // in the same second.
    const requestId = ++requestIdRef.current
    const controller = new AbortController()
    const isStale = () =>
      requestId !== requestIdRef.current || controller.signal.aborted
    const ctx = { isStale, signal: controller.signal }

    const flightCode = async query => {
      try {
        if (query !== '') {
          if (pickUpDate) {
            const pickUp = useDateAsIs
              ? pickUpDate
              : moment.utc(pickUpDate).format('YYYY-MM-DD')
            let searchQuery = query.toUpperCase()
            searchQuery = searchQuery.replace(/\s/g, '')
            await occurrenceDate(pickUp).then(value => {
              getFlightInformation(pickUp, searchQuery, value, 'pickUp', ctx)
            })
          } else if (dropOffAtoB) {
            const dropAtoB = useDateAsIs
              ? dropOffAtoB
              : moment.utc(dropOffAtoB).format('YYYY-MM-DD')
            let searchQuery = query.toUpperCase()
            searchQuery = searchQuery.replace(/\s/g, '')
            await occurrenceDate(dropAtoB).then(value => {
              getFlightInformation(dropAtoB, searchQuery, value, 'dropOff', ctx)
            })
          } else if (dropOffDate) {
            const dropOff = useDateAsIs
              ? dropOffDate
              : moment.utc(dropOffDate).format('YYYY-MM-DD')
            let searchQuery = query.toUpperCase()
            searchQuery = searchQuery.replace(/\s/g, '')
            await occurrenceDate(dropOff).then(value => {
              getFlightInformation(dropOff, searchQuery, value, 'dropOff', ctx)
            })
          } else {
            // No date to look the flight up against — pickUpDate, dropOffAtoB and
            // dropOffDate are ALL absent. Reachable whenever a parent mounts the card
            // before its booking data has settled, which the admin drop-off block does.
            //
            // The caller turned the field spinner ON immediately before invoking this
            // function, so falling out of the chain with no branch taken left it spinning
            // forever: no request in flight, and nothing downstream that would ever clear
            // it. That is the stuck loader on the admin add-on VIP drop-off block, still
            // visible with a fully resolved card underneath it.
            //
            // Only the spinner is undone. Deliberately NOT onFetchFlightData(null): the
            // flight number is fine and nothing was asked about it, so claiming
            // "not found" here would put a false error on the parent and a false entry in
            // #flight-tracking-errors. When the date arrives the effect re-runs and the
            // real lookup happens.
            onLoadingData(false)
          }
        } else {
          setNoData(true)
          onLoadingData(false)
          setFlightDataLoading(true)
          onFetchFlightData(null)
          setBookingLoading(true)
        }
      } catch (error) {
        logger({ fileName: 'flightTracking', error: error })
        console.log(error)
        if (isStale()) return
        onLoadingData(false)
        onFetchFlightData(null)
      }
    }

    // Only the fetch paths need a typing debounce; the cache-restore paths
    // below do zero network work and run on the next tick.
    const isFetchPath = Boolean(searchQuery || flightNumber)
    const timer = setTimeout(
      () => {
        // A half-typed flight number is not a lookup. The search runs as the user types,
        // so "BA830" also arrives as B / BA / BA8 / BA83, and an unresolvable query is the
        // MOST expensive kind we can send: it walks the resolver's entire fallback ladder
        // (/route, dated /airline, previous-day retry, parent-alias retry, no-date,
        // /schedules) before giving up, where a real flight resolves in one or two calls.
        // The same predicate already gated the Slack report; gating it here stops the
        // upstream calls the report was only telling us about.
        //
        // Skips the NETWORK, not the callback. The consumer is still told, exactly as it
        // is for a flight that genuinely resolves to nothing — see the identical block at
        // the `!newApiInfo` branch below and the warning attached to it: a caller that
        // never hears back leaves `isFetchingFlight` true and hangs the VIP Search button
        // forever. Clicking Search with a half-typed number is a real thing users do.
        //
        // So the on-screen behaviour is unchanged from today; the only difference is that
        // no FlightNerve credits were spent to reach it. The Slack report stays suppressed
        // by the same predicate in the parent's logFlightError.
        const pendingQuery = searchQuery || flightNumber
        if (pendingQuery && !isCompleteFlightNumber(pendingQuery)) {
          setNoData(true)
          setFlightDataLoading(false)
          onLoadingData(false)
          onFetchFlightData(null)
          return
        }
        if (searchQuery) {
          onFlightTrackingPickUp(null)
          onFlightTrackingDropOff(null)
          onLoadingData(true)
          flightCode(searchQuery)
        } else if (flightNumber) {
          onFlightTrackingPickUp(null)
          onFlightTrackingDropOff(null)
          onLoadingData(true)
          flightCode(flightNumber)
        } else if (flightTrackingPickUp) {
          if (!flightTrackingPickUp.noData) {
            setGetCity(flightTrackingPickUp.getCity)
            setDepartureTime(flightTrackingPickUp.departureTime)
            setArrivalTime(flightTrackingPickUp.arrivalTime)
            setDelayDepartureTime(flightTrackingPickUp.delayDepartureTime)
            setDelayArrivalTime(flightTrackingPickUp.delayArrivalTime)
            setFlightInfo(flightTrackingPickUp.flightInfo)
            setFlightTotalDuration(flightTrackingPickUp.flightTotalDuration)
            setProgressPercentage(flightTrackingPickUp.progressPercentage)
            setFlightDataLoading(false)
            setBookingLoading(false)
          }
        } else if (flightTrackingDropOff) {
          if (!flightTrackingDropOff.noData) {
            setGetCity(flightTrackingDropOff.getCity)
            setDepartureTime(flightTrackingDropOff.departureTime)
            setArrivalTime(flightTrackingDropOff.arrivalTime)
            setDelayDepartureTime(flightTrackingDropOff.delayDepartureTime)
            setDelayArrivalTime(flightTrackingDropOff.delayArrivalTime)
            setFlightInfo(flightTrackingDropOff.flightInfo)
            setFlightTotalDuration(flightTrackingDropOff.flightTotalDuration)
            setProgressPercentage(flightTrackingDropOff.progressPercentage)
            setFlightDataLoading(false)
            setBookingLoading(false)
          }
        } else if (!callApiPickUp) {
          if (Object.keys(pickUpCache).length) {
            for (let i = 0; i < pickUpCache.length; i++) {
              setExpanded(exisitingValues => ({
                ...exisitingValues,
                [i]: false,
              }))
            }

            let filteredResults = []
            if (pickUpCache.length && pickUpAddress) {
              const matchingFlight = pickUpCache.find(item =>
                addressMatchesFlightAirportInfo(
                  pickUpAddress,
                  item.getCity?.arrivalInfo
                )
              )

              filteredResults = matchingFlight ? [matchingFlight] : pickUpCache
            } else {
              filteredResults = pickUpCache
            }

            // A fresh pick-up fetch (searchQuery/flightNumber-driven) is authoritative over a
            // stale pickUpCache: once a pick-up flight has been fetched this session, don't let
            // the cached (possibly old-date) result overwrite it.
            if (
              !(
                lastFetchedFlightRef.current &&
                lastFetchedFlightRef.current.pickUp
              )
            ) {
              setAvailableFlights(filteredResults)
            }
            setFlightDataLoading(false)
            setBookingLoading(false)
          }
        } else if (!callApiDropOff) {
          if (Object.keys(dropOffCache).length) {
            for (let i = 0; i < dropOffCache.length; i++) {
              setExpanded(exisitingValues => ({
                ...exisitingValues,
                [i]: false,
              }))
            }
            let filteredResults = []
            if (dropOffCache.length && dropOffAddress) {
              const matchingFlight = dropOffCache.find(item =>
                addressMatchesFlightAirportInfo(
                  dropOffAddress,
                  item.getCity?.departureInfo
                )
              )

              filteredResults = matchingFlight ? [matchingFlight] : dropOffCache
            } else {
              filteredResults = dropOffCache
            }

            // Same as pick-up: a fresh drop-off fetch is authoritative over a stale dropOffCache.
            if (
              !(
                lastFetchedFlightRef.current &&
                lastFetchedFlightRef.current.dropOff
              )
            ) {
              setAvailableFlights(filteredResults)
            }
            setFlightDataLoading(false)
            setBookingLoading(false)
          }
        }
      },
      isFetchPath ? FETCH_DEBOUNCE_MS : 0
    )
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [
    searchQuery,
    flightNumber,
    dropOffAtoB,
    dropOffDate,
    pickUpDate,
    callApiPickUp,
    callApiDropOff,
    pickUpCache,
    dropOffCache,
  ])

  useEffect(() => {
    setOldQuery('')
  }, [pickUpDate, dropOffDate, dropOffAtoB])

  useEffect(() => {
    if (!flightDataLoading && availableFlights.length) {
      if (updateCache) {
        if (pickUpDate) {
          cachePickUpInfo(availableFlights, type)
        } else {
          cacheDropOffInfo(availableFlights, type)
        }
      }

      if (!flightTrackingPickUp || flightTrackingPickUp) {
        onFlightTrackingPickUp(
          {
            getCity,
            departureTime,
            arrivalTime,
            delayDepartureTime,
            flightInfo,
            flightTotalDuration,
            progressPercentage,
            delayArrivalTime,
            noData,
            time,
          },
          type
        )
      }
      if (!flightTrackingDropOff || flightTrackingDropOff) {
        onFlightTrackingDropOff(
          {
            getCity,
            departureTime,
            arrivalTime,
            delayDepartureTime,
            flightInfo,
            flightTotalDuration,
            progressPercentage,
            delayArrivalTime,
            noData,
            time,
          },
          type
        )
      }
    }
  }, [
    progressPercentage,
    flightDataLoading,
    noData,
    flightTrackingPickUp,
    flightTrackingDropOff,
    updateCache,
    availableFlights,
  ])

  // Verified = the shared rule (flightNervePure.isFlightVerifiedForBooking): at the booked
  // airport and on the booked day, across midnight only by a short margin.
  const isPickUpLeg = Boolean(airportPickUpInfo)
  const bookedTimeForLeg =
    verificationDate ||
    (isPickUpLeg
      ? pickUpDate || airportPickUpInfo?.date
      : dropOffDate || airportDropOffInfo?.date)
  const bookedIata = isPickUpLeg ? pickUpAirportIata : dropOffAirportIata
  const bookedAddress = isPickUpLeg ? pickUpAddress : dropOffAddress
  const flightsToShow = useMemo(
    () =>
      verifiedFlightsOnly
        ? availableFlights.filter(item =>
            isFlightVerifiedForBooking({
              airportInfo: isPickUpLeg
                ? item?.getCity?.arrivalInfo
                : item?.getCity?.departureInfo,
              flightTime: isPickUpLeg
                ? item?.time?.arrival
                : item?.time?.departure,
              side: isPickUpLeg ? 'arrival' : 'departure',
              bookedAirport: { iata: bookedIata, fullAddress: bookedAddress },
              bookedTime: bookedTimeForLeg,
            })
          )
        : availableFlights,
    [
      verifiedFlightsOnly,
      availableFlights,
      isPickUpLeg,
      bookedIata,
      bookedAddress,
      bookedTimeForLeg,
    ]
  )
  const hasVerifiedFlight = verifiedFlightsOnly && flightsToShow.length > 0
  useEffect(() => {
    if (verifiedFlightsOnly && onVerifiedFlightsChange) {
      onVerifiedFlightsChange(hasVerifiedFlight)
    }
  }, [verifiedFlightsOnly, hasVerifiedFlight]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`${!noMarginTop ? 'mt-3' : ''} ${
        flightsToShow && flightsToShow.length && !hideResultsCard
          ? 'flight-card'
          : ''
      }`}
    >
      {showSkeletonLoading && isLoading ? (
        <Skeleton height={65} />
      ) : flightsToShow.length && !hideResultsCard ? (
        flightsToShow.map((item, index) => {
          return (
            <div
              // Stable identity, not the array position. The list is replaced wholesale
              // per lookup so an index key was not actively breaking anything, but it ties
              // the DOM node to a slot rather than to a flight; keyed on the flight it
              // survives a re-render that swaps the payload underneath.
              key={flightKeyFromProcessedFlightItem(item) || `flight-${index}`}
              className={alwaysExpanded ? 'vip-always-expanded-flight' : ''}
              style={
                alwaysExpanded
                  ? {
                      borderTop: '1.5px solid rgba(139, 149, 158, 0.1)',
                      borderBottom: '1.5px solid rgba(139, 149, 158, 0.1)',
                    }
                  : undefined
              }
            >
              {!alwaysExpanded && (
                <span
                  className={`flight-header expandable ${
                    expanded[index] ? 'expanded' : ''
                  } ${index !== 0 ? 'flight-border-top-none' : ''}`}
                  onClick={() => {
                    setExpanded({
                      ...expanded,
                      [index]: !expanded[index],
                    })
                  }}
                >
                  <span className='mb-1'>{`${item.departureTime.time.hours}:${item.departureTime.time.scheduleMinutes}`}</span>
                  <span>{`${item.flightInfo.airline.iata} ${item.flightInfo.airline.number}`}</span>
                  {item.flightInfo ? (
                    <>
                      {item.flightInfo &&
                      item.flightInfo.flight_status === 'Arrived' ? (
                        <div className='flightStatus'>ARRIVED</div>
                      ) : item.flightInfo.flight_status === 'scheduled' ||
                        item.flightInfo.flight_status === 'Scheduled' ? (
                        <div className='flightStatus'>SCHEDULED</div>
                      ) : item.flightInfo.flight_status === 'Delayed' ? (
                        <div className='flight-delayed'>Delayed</div>
                      ) : (
                        <div className='flightStatus'>ON TIME</div>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  <span className='d-flex'>
                    {`${item.getCity.departureInfo.city}`}
                    <span className='flight-header-iata'>{`${item.getCity.departureInfo.iata_faa}`}</span>
                  </span>
                </span>
              )}
              {alwaysExpanded || expanded[index] ? (
                <FlightTrackingCard
                  key={`${index}`}
                  getCity={item.getCity}
                  departureTime={item.departureTime}
                  arrivalTime={item.arrivalTime}
                  delayDepartureTime={item.delayDepartureTime}
                  flightInfo={item.flightInfo}
                  flightTotalDuration={item.flightTotalDuration}
                  progressPercentage={item.progressPercentage}
                  delayArrivalTime={item.delayArrivalTime}
                  airportPickUpInfo={
                    airportPickUpInfo
                      ? {
                          ...airportPickUpInfo,
                          terminal:
                            airportPickUpInfo.terminal ||
                            (terminals?.arrival
                              ? 'T' + terminals.arrival
                              : null),
                        }
                      : airportPickUpInfo
                  }
                  airportDropOffInfo={
                    airportDropOffInfo
                      ? {
                          ...airportDropOffInfo,
                          terminal:
                            airportDropOffInfo.terminal ||
                            (terminals?.departure
                              ? 'T' + terminals.departure
                              : null),
                        }
                      : airportDropOffInfo
                  }
                  noData={item.noData}
                  time={item.time}
                  flightDataLoading={item.flightDataLoading}
                  index={index}
                  numberOfFlights={flightsToShow.length}
                />
              ) : (
                <></>
              )}
            </div>
          )
        })
      ) : (
        <></>
      )}
    </div>
  )
}

export default FlightTracking
